import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, Plus, Minus, ArrowRight, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { alignClass } from "../lib/align";
import { useSettings } from "../context/SettingsContext";
import { Button, Reveal, SmartImage, SectionHeading, Counter, DynamicIcon } from "../components/ui";
import ProjectCard from "../components/ProjectCard";
import Lightbox from "../components/Lightbox";
import SwipeRow from "../components/SwipeRow";
import Partners from "../components/Partners";
import PlanWizard from "../components/PlanWizard";
import FindDesign from "../components/FindDesign";
import Brochures from "../components/Brochures";
import LeadForm from "../components/LeadForm";
import SEO from "../components/SEO";
import { optimg } from "../lib/img";

const FILTERS = ["All", "Elevation", "Interior", "Floor Plan", "3D Floor Plan", "Construction", "Completed Projects"];

export const SECTION_DEFS = [
  { key: "hero", label: "Hero" },
  { key: "stats", label: "Statistics" },
  { key: "featured", label: "Featured Projects", kicker: "Our Work", heading: "Featured Projects", subtitle: "From concept to completion, we deliver projects that stand the test of time." },
  { key: "elevations", label: "Elevations", kicker: "Exteriors", heading: "Latest Elevation Designs" },
  { key: "interiors", label: "Interiors", kicker: "Interiors", heading: "Interior Designs" },
  { key: "floorplans", label: "Floor Plans", kicker: "Planning", heading: "Smart Floor Plans" },
  { key: "services", label: "Services", kicker: "Our Services", heading: "We Provide Best Services", subtitle: "Comprehensive architecture, interior and construction solutions under one roof." },
  { key: "why", label: "Why GNONS", kicker: "Why GNONS", heading: "Why Choose GNONS", subtitle: "Trusted for quality, transparency and on-time delivery." },
  { key: "process", label: "Process", kicker: "Our Approach", heading: "A Proven Process to Deliver Excellence", subtitle: "" },
  { key: "testimonials", label: "Testimonials", kicker: "Testimonials", heading: "What Our Clients Say", subtitle: "" },
  { key: "tools", label: "Find Design + Calculator" },
  { key: "plan", label: "Plan Your Dream Home" },
  { key: "brochures", label: "Brochures" },
  { key: "partners", label: "Partners" },
  { key: "cta", label: "Contact CTA" },
  { key: "faq", label: "FAQ" },
];

export default function Home() {
  const { settings, content } = useSettings();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [filter, setFilter] = useState("All");
  const [visible, setVisible] = useState(8);
  const [lb, setLb] = useState({ items: [], index: null });
  const [openFaq, setOpenFaq] = useState(0);
  const [planOpen, setPlanOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/projects?homepage=true&limit=48")
      .then((r) => { if (alive) setProjects(r.data.items || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    api.get("/services").then((r) => alive && setServices(r.data || [])).catch(() => {});
    api.get("/testimonials").then((r) => alive && setTestimonials(r.data || [])).catch(() => {});
    return () => { alive = false; };
  }, []);

  const featuredProjects = useMemo(() => projects.filter((p) => p.featured), [projects]);
  const filtered = useMemo(() => {
    if (filter === "All") return featuredProjects;
    return featuredProjects.filter((p) => (p.tags || []).includes(filter));
  }, [featuredProjects, filter]);

  const byTag = (tag) => projects.filter((p) => (p.tags || []).includes(tag));
  const elevations = byTag("Elevation").slice(0, 6);
  const interiors = byTag("Interior").slice(0, 6);
  const floorplans = projects.filter((p) => (p.tags || []).some((t) => t === "Floor Plan" || t === "3D Floor Plan")).slice(0, 6);

  const openLightbox = (list, i) => setLb({ items: list.map((p) => ({ url: p.cover_image, name: p.name, category: p.tags?.[0] || p.category, slug: p.slug })), index: i });

  const stats = content.stats || [];
  const why = content.why_choose || [];
  const process = content.process || [];
  const faqs = content.faqs || [];

  const secCfg = content.sections || {};
  const defOrder = Object.fromEntries(SECTION_DEFS.map((d, i) => [d.key, i]));
  const ord = (k) => Number(secCfg[k]?.order ?? defOrder[k]);
  const on = (k) => secCfg[k]?.enabled !== false;
  const sh = (k) => {
    const d = SECTION_DEFS.find((x) => x.key === k) || {};
    const c = secCfg[k] || {};
    return { kicker: c.kicker || d.kicker, title: c.heading || d.heading, subtitle: c.subtitle !== undefined ? c.subtitle : d.subtitle };
  };

  return (
    <>
      <SEO
        jsonLd={{
          "@context": "https://schema.org", "@type": "LocalBusiness", name: settings.company_name,
          image: settings.hero_image, "@id": settings.website, url: settings.website, telephone: settings.phone,
          address: { "@type": "PostalAddress", streetAddress: settings.address, addressLocality: "Indore", addressRegion: "MP", addressCountry: "IN" },
        }}
      />

      <div className="flex flex-col">
        {/* HERO */}
        {on("hero") && (
          <section style={{ order: ord("hero") }} className="relative flex items-center pt-28 pb-14 lg:pt-32 lg:pb-16 lg:min-h-[80vh] overflow-hidden bg-sand">
            <div className="container-x grid lg:grid-cols-2 gap-10 lg:gap-16 items-center relative z-10">
              <div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="kicker mb-5">
                  {content.hero_kicker || "We Design • We Build • We Deliver"}
                </motion.div>
                <h1 className="font-extrabold leading-[1.06] text-ink" style={{ fontSize: "clamp(2rem, 2.8vw, 3.3rem)" }}>
                  <motion.span initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }} className="block">{content.hero_heading_1 || "Building the Future"}</motion.span>
                  <motion.span initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="block text-gold">{content.hero_heading_2 || "with Smart Design &"}</motion.span>
                  <motion.span initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }} className="block">{content.hero_heading_3 || "Quality Construction"}</motion.span>
                </h1>
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className={`mt-6 text-muted text-base md:text-lg max-w-md ${alignClass(content.hero_subheading_align)}`}>
                  {content.hero_subheading || "Architecture | Construction | Interior | Renovation | Turnkey Projects"}
                </motion.p>
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-8 flex flex-wrap gap-3">
                  <Button to="/contact" variant="primary" data-testid="hero-consultation-btn">{content.hero_cta || "Get Free Consultation"}</Button>
                  <Button to="/projects" variant="outline" data-testid="hero-projects-btn">{content.hero_cta_secondary || "View Our Projects"}</Button>
                </motion.div>
              </div>
              <motion.div initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="relative">
                <div className="relative rounded-[1.8rem] overflow-hidden shadow-lift aspect-[4/3] lg:aspect-[5/4] lg:max-h-[520px] border border-line bg-beige">
                  <SmartImage src={content.hero_image || settings.hero_image} alt="GNONS featured project" w={1000} eager />
                </div>
                <div className="absolute -bottom-5 -left-3 lg:-left-8 glass border border-line rounded-2xl px-5 py-4 shadow-soft hidden sm:block">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-gold/15 flex items-center justify-center"><DynamicIcon name="Building2" className="text-gold" size={22} /></div>
                    <div>
                      <div className="text-xl font-bold text-ink leading-none">{stats[0]?.value || "500+"}</div>
                      <div className="text-xs text-muted mt-1">{stats[0]?.label || "Projects Completed"}</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>
        )}

        {/* STATS */}
        {on("stats") && (
          <section style={{ order: ord("stats") }} className="bg-white border-y border-line">
            <div className="container-x grid grid-cols-2 lg:grid-cols-4 divide-x divide-line">
              {stats.map((s, i) => (
                <Reveal key={i} delay={i * 80} className="py-8 md:py-12 px-4 md:px-8 text-center">
                  <span className="mx-auto mb-4 w-16 h-16 rounded-full bg-gold/10 ring-1 ring-gold/20 flex items-center justify-center">
                    <DynamicIcon name={s.icon} className="text-gold" size={32} />
                  </span>
                  <div className="text-2xl md:text-4xl font-extrabold text-ink"><Counter value={s.value} /></div>
                  <div className="text-xs md:text-sm text-muted mt-1.5">{s.label}</div>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {/* SHOWCASE */}
        {on("featured") && (
          <section style={{ order: ord("featured") }} className="py-16 md:py-20 bg-sand" id="work">
            <div className="container-x">
              <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                <SectionHeading kicker={sh("featured").kicker} title={sh("featured").title} subtitle={sh("featured").subtitle} />
                <Button to="/projects" variant="ghost" className="self-start md:self-end">Explore All Projects</Button>
              </Reveal>

              <div className="flex gap-2 md:gap-3 overflow-x-auto no-scrollbar pb-2 mb-8 -mx-5 px-5 md:mx-0 md:px-0" data-testid="home-filters">
                {FILTERS.map((f) => (
                  <button
                    key={f}
                    onClick={() => { setFilter(f); setVisible(8); }}
                    data-testid={`filter-${f.toLowerCase().replace(/\s/g, "-")}`}
                    className={`shrink-0 px-5 py-2.5 rounded-full text-sm font-medium border transition-all duration-300 ${filter === f ? "bg-ink text-white border-ink" : "bg-white text-ink border-line hover:border-gold hover:text-gold"}`}
                  >
                    {f}
                  </button>
                ))}
              </div>

              {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6" data-testid="showcase-skeleton">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="rounded-xl2 border border-line bg-beige img-blur aspect-[4/5]" />
                  ))}
                </div>
              ) : filtered.length === 0 ? (
                <p className="text-center text-muted py-12" data-testid="showcase-empty">No projects in this category yet.</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6" data-testid="showcase-grid">
                  {filtered.slice(0, visible).map((p) => (
                    <Reveal key={p.id}>
                      <ProjectCard project={p} cursorFollow={p.featured} onOpen={() => openLightbox(filtered, filtered.indexOf(p))} aspect="aspect-[4/5]" className="h-full" />
                    </Reveal>
                  ))}
                </div>
              )}

              {visible < filtered.length && (
                <div className="text-center mt-10">
                  <Button variant="dark" icon={false} onClick={() => setVisible((v) => v + 8)} data-testid="load-more-btn">Load More Projects</Button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ELEVATIONS */}
        {on("elevations") && elevations.length > 0 && (
          <div style={{ order: ord("elevations") }}>
            <Section title={sh("elevations").title} kicker={sh("elevations").kicker} subtitle={sh("elevations").subtitle} cta={["View More Elevations", "/projects?tag=Elevation"]}>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="elevations-grid">
                {elevations.map((p) => <ProjectCard key={p.id} project={p} cursorFollow={p.featured} onOpen={() => openLightbox(elevations, elevations.indexOf(p))} aspect="aspect-[4/5]" />)}
              </div>
            </Section>
          </div>
        )}

        {/* INTERIORS */}
        {on("interiors") && interiors.length > 0 && (
          <div style={{ order: ord("interiors") }}>
            <Section dark title={sh("interiors").title} kicker={sh("interiors").kicker} subtitle={sh("interiors").subtitle} cta={["Explore More Interiors", "/projects?tag=Interior"]}>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="interiors-grid">
                {interiors.map((p) => <ProjectCard key={p.id} project={p} onOpen={() => openLightbox(interiors, interiors.indexOf(p))} aspect="aspect-[4/3]" />)}
              </div>
            </Section>
          </div>
        )}

        {/* FLOOR PLANS */}
        {on("floorplans") && floorplans.length > 0 && (
          <div style={{ order: ord("floorplans") }}>
            <Section title={sh("floorplans").title} kicker={sh("floorplans").kicker} subtitle={sh("floorplans").subtitle} cta={["View More Floor Plans", "/projects?tag=Floor Plan"]}>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="floorplans-grid">
                {floorplans.map((p) => <ProjectCard key={p.id} project={p} onOpen={() => openLightbox(floorplans, floorplans.indexOf(p))} aspect="aspect-[4/5]" />)}
              </div>
            </Section>
          </div>
        )}

        {/* SERVICES */}
        {on("services") && (
          <section style={{ order: ord("services") }} className="py-16 md:py-20 bg-white">
            <div className="container-x">
              <Reveal><SectionHeading center kicker={sh("services").kicker} title={sh("services").title} subtitle={sh("services").subtitle} /></Reveal>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12 max-w-6xl mx-auto">
                {services.slice(0, 6).map((s, i) => (
                  <Reveal key={s.id} delay={i * 70}>
                    <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="group block h-full rounded-xl2 border border-line bg-white p-7 hover:shadow-lift hover:-translate-y-1 transition-all duration-400">
                      <div className="w-14 h-14 rounded-xl bg-beige flex items-center justify-center group-hover:bg-gold transition-colors duration-400">
                        <DynamicIcon name={s.icon} className="text-gold group-hover:text-white transition-colors" size={26} />
                      </div>
                      <h3 className="mt-5 text-lg font-bold text-ink">{s.name}</h3>
                      <p className="mt-2 text-sm text-muted leading-relaxed">{s.short_description}</p>
                      <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-gold">Learn More <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" /></span>
                    </Link>
                  </Reveal>
                ))}
              </div>
              <div className="text-center mt-10"><Button to="/services" variant="outline">View All Services</Button></div>
            </div>
          </section>
        )}

        {/* WHY GNONS */}
        {on("why") && (
          <section style={{ order: ord("why") }} className="py-16 md:py-20 bg-sand">
            <div className="container-x">
              <Reveal><SectionHeading center kicker={sh("why").kicker} title={sh("why").title} subtitle={sh("why").subtitle} /></Reveal>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12 max-w-6xl mx-auto">
                {why.map((w, i) => (
                  <Reveal key={i} delay={i * 60}>
                    <div className="flex gap-4 rounded-xl2 bg-white border border-line p-6 h-full hover:shadow-soft transition-shadow">
                      <div className="w-12 h-12 shrink-0 rounded-full bg-gold/10 flex items-center justify-center"><DynamicIcon name={w.icon} className="text-gold" size={22} /></div>
                      <div>
                        <h3 className="font-bold text-ink">{w.title}</h3>
                        <p className="text-sm text-muted mt-1.5 leading-relaxed">{w.desc}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* PROCESS */}
        {on("process") && (
          <section style={{ order: ord("process") }} className="py-16 md:py-20 bg-white">
            <div className="container-x">
              <Reveal><SectionHeading center kicker={sh("process").kicker} title={sh("process").title} subtitle={sh("process").subtitle} /></Reveal>
              <div className="mt-14 relative">
                <div className="hidden lg:block absolute top-7 left-0 right-0 h-px bg-line" />
                <div className="grid lg:grid-cols-6 gap-8 lg:gap-4">
                  {process.map((p, i) => (
                    <Reveal key={i} delay={i * 90} className="relative flex lg:flex-col gap-4 lg:gap-0 lg:text-center">
                      <div className="relative z-10 w-14 h-14 shrink-0 rounded-full bg-ink text-white flex items-center justify-center font-bold lg:mx-auto lg:mb-5">
                        <span>{p.step}</span>
                      </div>
                      <div>
                        <DynamicIcon name={p.icon} className="text-gold mb-2 lg:mx-auto hidden lg:block" size={22} />
                        <h3 className="font-bold text-ink">{p.title}</h3>
                        <p className="text-sm text-muted mt-1.5">{p.desc}</p>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* TESTIMONIALS */}
        {on("testimonials") && testimonials.length > 0 && (
          <section style={{ order: ord("testimonials") }} className="py-16 md:py-20 bg-sand">
            <div className="container-x">
              <Reveal><SectionHeading center kicker={sh("testimonials").kicker} title={sh("testimonials").title} subtitle={sh("testimonials").subtitle} /></Reveal>
              <div className="grid md:grid-cols-3 gap-5 mt-12 max-w-6xl mx-auto">
                {testimonials.slice(0, 6).map((t, i) => (
                  <Reveal key={t.id} delay={i * 80}>
                    <div className="rounded-xl2 bg-white border border-line p-7 h-full relative">
                      <Quote size={30} className="text-gold/25 absolute top-5 right-5" />
                      <div className="flex gap-0.5 mb-4">{Array.from({ length: t.rating || 5 }).map((_, k) => <Star key={k} size={15} className="fill-gold text-gold" />)}</div>
                      <p className="text-sm text-ink/80 leading-relaxed">"{t.review}"</p>
                      <div className="mt-5 pt-4 border-t border-line flex items-center gap-4">
                        {t.photo_url
                          ? <img src={optimg(t.photo_url, { w: 160, h: 160 })} alt={t.client_name} className="w-16 h-16 rounded-full object-cover ring-2 ring-gold/20 shrink-0" referrerPolicy="no-referrer" />
                          : <span className="w-16 h-16 rounded-full bg-beige text-gold font-bold flex items-center justify-center text-xl ring-2 ring-gold/20 shrink-0">{(t.client_name || "?").trim().charAt(0).toUpperCase()}</span>}
                        <div>
                          <div className="font-bold text-ink">{t.client_name}</div>
                          {(t.service || t.project_name) && <div className="text-xs text-gold font-semibold mt-0.5">{[t.service, t.project_name].filter(Boolean).join(" · ")}</div>}
                          {t.city && <div className="text-xs text-muted mt-0.5">{t.city}</div>}
                        </div>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* TOOLS: FIND DESIGN + CALCULATOR */}
        {on("tools") && <div style={{ order: ord("tools") }}><FindDesign /></div>}

        {/* PLAN YOUR DREAM HOME */}
        {on("plan") && (
          <section style={{ order: ord("plan") }} className="py-16 md:py-20 bg-beige" id="plan">
            <div className="container-mid px-5 md:px-8">
              <div className="rounded-[1.8rem] bg-ink text-white p-8 md:p-14 relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, #C88A32 0, transparent 45%)" }} />
                <div className="relative max-w-2xl">
                  <div className="kicker mb-3">Free Design Consultation</div>
                  <h2 className="text-3xl md:text-[2.7rem] font-bold leading-tight">Plan Your Dream Home in Minutes</h2>
                  <p className="mt-4 text-white/65 max-w-lg">Tell us your plot size, facing, requirement, budget and timeline — our team will get back with tailored design options.</p>
                  <button onClick={() => setPlanOpen(true)} data-testid="open-plan-wizard" className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold text-white font-semibold px-7 py-3.5 shadow-gold hover:bg-gold-dark transition-colors">
                    Start Planning <ArrowRight size={17} />
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* BROCHURES */}
        {on("brochures") && <div style={{ order: ord("brochures") }}><Brochures /></div>}

        {/* PARTNERS */}
        {on("partners") && <div style={{ order: ord("partners") }}><Partners /></div>}

        {/* LEAD CTA */}
        {on("cta") && (
          <section style={{ order: ord("cta") }} className="py-16 md:py-20 bg-ink text-white">
            <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="kicker mb-4">Start Your Project</div>
                <h2 className="text-3xl md:text-[2.7rem] font-bold leading-tight">Planning to Build Your Dream Project?</h2>
                <p className="mt-4 text-white/60 max-w-md">Get a free consultation and expert guidance for your dream home or commercial space. No obligation, complete transparency.</p>
                <div className="mt-8 space-y-3">
                  {["Expert & Experienced Team", "Premium Quality", "On-Time Delivery", "Transparent Pricing"].map((f) => (
                    <div key={f} className="flex items-center gap-3 text-sm text-white/80"><CheckCircle2 size={18} className="text-gold" /> {f}</div>
                  ))}
                </div>
              </div>
              <div className="rounded-xl2 bg-white/5 border border-white/10 p-6 md:p-8">
                <h3 className="text-lg font-bold mb-5">Book Free Consultation</h3>
                <LeadForm sourcePage="Home CTA" dark />
              </div>
            </div>
          </section>
        )}

        {/* FAQ */}
        {on("faq") && faqs.length > 0 && (
          <section style={{ order: ord("faq") }} className="py-16 md:py-20 bg-white">
            <div className="container-x max-w-3xl">
              <Reveal><SectionHeading center kicker="FAQ" title="Frequently Asked Questions" /></Reveal>
              <div className="mt-10 divide-y divide-line border-y border-line">
                {faqs.map((f, i) => (
                  <div key={i} data-testid={`faq-${i}`}>
                    <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 py-5 text-left">
                      <span className="font-semibold text-ink">{f.q}</span>
                      {openFaq === i ? <Minus size={20} className="text-gold shrink-0" /> : <Plus size={20} className="text-muted shrink-0" />}
                    </button>
                    <AnimatePresence>
                      {openFaq === i && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                          <p className="pb-5 text-muted leading-relaxed text-[15px]">{f.a}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>

      <Lightbox items={lb.items} index={lb.index} onClose={() => setLb({ items: [], index: null })} onIndex={(i) => setLb((s) => ({ ...s, index: i }))} />
      <PlanWizard open={planOpen} onClose={() => setPlanOpen(false)} />
    </>
  );
}

function Section({ title, kicker, subtitle, cta, children, dark }) {
  return (
    <section className={`py-16 md:py-20 ${dark ? "bg-ink text-white" : "bg-white"}`}>
      <div className="container-x">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-10">
          <SectionHeading kicker={kicker} title={title} subtitle={subtitle} light={dark} />
          {cta && <Button to={cta[1]} variant={dark ? "light" : "ghost"} className="self-start md:self-end">{cta[0]}</Button>}
        </Reveal>
        {children}
      </div>
    </section>
  );
}
